import type {HttpStatusCode} from '../../types/web/http';

class DefaultHttpStatusCode implements HttpStatusCode {
    readonly #value:  number;
    constructor(value: number) {
        this.#value = value;
    }
    get value(): number {
        return this.#value;
    }

    toString(): string {
        return this.#value.toString();
    }
}

export class HttpStatus implements HttpStatusCode {
    static readonly CONTINUE = new HttpStatus(100, 'Continue');
    static readonly SWITCHING_PROTOCOLS = new HttpStatus(101, 'Switching Protocols');

    // 2xx Success

    static readonly OK = new HttpStatus(200, 'OK');
    static readonly CREATED = new HttpStatus(201, 'Created');
    static readonly ACCEPTED = new HttpStatus(202, 'Accepted');
    static readonly  NON_AUTHORITATIVE_INFORMATION = new HttpStatus(203, 'Non-Authoritative Information');
    static readonly NO_CONTENT = new HttpStatus(204, 'No Content');
    static readonly RESET_CONTENT = new HttpStatus(205, 'Reset Content');
    static readonly PARTIAL_CONTENT = new HttpStatus(206, 'Partial Content');
    static readonly MULTI_STATUS = new HttpStatus(207, 'Multi-Status');

    static readonly IM_USED = new HttpStatus(226, 'IM Used');


    // 3xx Redirection
    static readonly MULTIPLE_CHOICES = new HttpStatus(300, 'Multiple Choices');
    static readonly MOVED_PERMANENTLY = new HttpStatus(301, 'Moved Permanently');

    // 4xx Client Error

    static readonly BAD_REQUEST = new HttpStatus(400, 'Bad Request');
    static readonly UNAUTHORIZED = new HttpStatus(401, 'Unauthorized');
    static readonly FORBIDDEN = new HttpStatus(403, 'Forbidden');
    static readonly NOT_FOUND = new HttpStatus(404, 'Not Found');
    static readonly METHOD_NOT_ALLOWED = new HttpStatus(405, 'Method Not Allowed');
    static readonly NOT_ACCEPTABLE = new HttpStatus(406, 'Not Acceptable');
    static readonly PROXY_AUTHENTICATION_REQUIRED = new HttpStatus(407, 'Proxy Authentication Required');
    static readonly REQUEST_TIMEOUT = new HttpStatus(408, 'Request Timeout');
    static readonly CONFLICT = new HttpStatus(409, 'Conflict');
    static readonly GONE = new HttpStatus(410, 'Gone');
    static readonly LENGTH_REQUIRED = new HttpStatus(411, 'Length Required');
    static readonly PRECONDITION_FAILED = new HttpStatus(412, 'Precondition Failed');
    static readonly PAYLOAD_TOO_LARGE = new HttpStatus(413, 'Payload Too Large');
    static readonly URI_TOO_LONG = new HttpStatus(414, 'URI Too Long');
    static readonly UNSUPPORTED_MEDIA_TYPE = new HttpStatus(415, 'Unsupported Media Type');

    static readonly EXPECTATION_FAILED = new HttpStatus(417, 'Expectation Failed');
    static readonly IM_A_TEAPOT = new HttpStatus(418, 'I\'m a teapot');

    static readonly TOO_EARLY = new HttpStatus(425, 'Too Early');
    static readonly UPGRADE_REQUIRED = new HttpStatus(426, 'Upgrade Required');
    static readonly PRECONDITION_REQUIRED = new HttpStatus(428, 'Precondition Required');
    static readonly TOO_MANY_REQUESTS = new HttpStatus(429, 'Too Many Requests');
    static readonly REQUEST_HEADER_FIELDS_TOO_LARGE = new HttpStatus(431, 'Request Header Fields Too Large');
    static readonly UNAVAILABLE_FOR_LEGAL_REASONS = new HttpStatus(451, 'Unavailable For Legal Reasons');

    // 5xx Server Error
    static readonly INTERNAL_SERVER_ERROR = new HttpStatus(500, 'Internal Server Error');
    static readonly NOT_IMPLEMENTED = new HttpStatus(501, 'Not Implemented');
    static readonly BAD_GATEWAY = new HttpStatus(502, 'Bad Gateway');
    static readonly SERVICE_UNAVAILABLE = new HttpStatus(503, 'Service Unavailable');
    static readonly GATEWAY_TIMEOUT = new HttpStatus(504, 'Gateway Timeout');
    static readonly HTTP_VERSION_NOT_SUPPORTED = new HttpStatus(505, 'HTTP Version Not Supported');
    static readonly VARIANT_ALSO_NEGOTIATES = new HttpStatus(506, 'Variant Also Negotiates');
    static readonly INSUFFICIENT_STORAGE = new HttpStatus(507, 'Insufficient Storage');
    static readonly LOOP_DETECTED = new HttpStatus(508, 'Loop Detected');
    static readonly NOT_EXTENDED = new HttpStatus(510, 'Not Extended');
    static readonly NETWORK_AUTHENTICATION_REQUIRED = new HttpStatus(511, 'Network Authentication Required');

    static readonly #VALUES: HttpStatus[] = [];
    static {
        Object.keys(HttpStatus)
            .filter(key => key !== 'VALUES' && key !== 'resolve')
            .forEach(key => {
                const value = HttpStatus[key];
                if (value instanceof HttpStatus) {
                    Object.defineProperty(value, 'name', { enumerable: true, value: key, writable: false });
                    HttpStatus.#VALUES.push(value);
                }
            })

    }

    static resolve(code: number): HttpStatus | undefined {
        for (const status of HttpStatus.#VALUES) {
            if (status.value === code) {
                return status;
            }
        }
    }

    readonly #value: number;
    readonly #phrase: string;

    private constructor(value: number, phrase: string) {
        this.#value = value;
        this.#phrase = phrase;
    }

    get value(): number {
        return this.#value;
    }
    get phrase(): string {
        return this.#phrase;
    }

    toString() {
        return `${this.#value} ${this['name']}`;
    }
}


export function httpStatusCode(value: number | HttpStatusCode): HttpStatusCode {
    if (typeof value === 'number') {
        if (value < 100 || value > 999) {
            throw new Error(`status code ${value} should be in range 100-999`);
        }
        const status = HttpStatus.resolve(value);
        if (status !== undefined) {
            return status;
        }
        return new DefaultHttpStatusCode(value);
    }
    return value;
}
